import * as THREE from 'three';
import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span, type Ease } from '../curves.ts';
import { apply, timeline, type Beat, type KeyPose } from '../pose.ts';

/*
 * Hondero trasgo (models/slinger.ts). Idle (a lazy twirl of the sling over its head) and walk (a skulking trot,
 * the sling still turning) loop; attack comes in already mid-whirl, the sling speeds up and lets the stone go at
 * ~0.13 s (when the simulation launches it; the pouch is then out to the right of the fist, swinging forward),
 * follows through and reloads from the bag; hit; stun (loop, the sling hanging); die (ends on the ground).
 *
 * The sling spins about the fist on its `sling` bone: a negative Y turn swings the pouch forward on the right side.
 * Its fiery streak is the `trail` bone (scale 0 hides it, 1 is a hard whirl) and the stone the `stone` bone.
 *
 * Conventions as in human.ts: limbs hang along -Y, so a negative X swings an arm or leg forward; +X on a shin
 * bends the knee back; +X on a trunk leans it forward; +X on a jaw opens it; +Y turns a trunk to its right (+X);
 * +Z on the right arm lifts it out to the side (it is modelled raised, so -Z lowers it).
 */

type B = 'root' | 'body' | 'bag' | 'chest' | 'head' | 'jaw' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'sling' | 'stone' | 'trail' | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

/** Crouched and coiled, knees bowed, the head low and thrust forward, the free claw by the bag. */
const ST: P = {
  root: [0, 0, 0, 0, -0.035, 0],
  body: [0.1, 0, 0],
  chest: [0.08, 0, 0],
  head: [-0.14, 0, 0],
  armL: [-0.1, 0, -0.05],
  foreL: [-0.2, 0, 0],
  legL: [-0.22, 0, -0.06],
  legR: [-0.22, 0, 0.06],
  shinL: [0.34, 0, 0],
  shinR: [0.34, 0, 0],
};

/** Adds the stance to every beat (so a timeline's beats only spell what changes from it). */
const over = (beats: readonly (readonly [number, P, Ease?])[]): Beat<B>[] =>
  beats.map(([t, p, e]) => (e ? [t, { ...ST, ...p }, e] : [t, { ...ST, ...p }]) as Beat<B>);

/**
 * The whirl: the sling turned to `theta` (radians; decreasing is forward on the right), the disc tipped `tilt`
 * toward the front, the fist circling a little ahead of the pouch (`lead` of the forearm), the streak at `fire`.
 */
const whirl = (b: Bones<B>, theta: number, tilt: number, lead: number, fire: number): void => {
  b.sling.rot(tilt, theta, 0);
  // The fist pulls the pouch round: it runs a quarter turn ahead, tracing a small circle.
  const a = theta - Math.PI / 2;
  b.foreR.rot(lead * Math.sin(a), 0, lead * Math.cos(a));
  b.armR.rot(0.4 * lead * Math.sin(a - 0.5), 0, 0.4 * lead * Math.cos(a - 0.5));
  b.trail.scale(Math.max(0.001, fire));
};

const _q = new THREE.Quaternion();
const _c = new THREE.Quaternion();
const _e = new THREE.Euler();
const _d = new THREE.Vector3();
const X = new THREE.Vector3(1, 0, 0);
const CHAIN: readonly B[] = ['root', 'body', 'chest', 'armR', 'foreR'];

/** Lets the sling hang from the fist toward model-space `dir` whatever the arm is doing (stun, death). */
const hang = (b: Bones<B>, dir: THREE.Vector3): void => {
  _c.identity();
  for (const n of CHAIN) _c.multiply(_q.setFromEuler(_e.set(b[n].rx, b[n].ry, b[n].rz, 'XYZ')));
  _q.setFromUnitVectors(X, _d.copy(dir).normalize());
  _c.invert().multiply(_q);
  _e.setFromQuaternion(_c, 'XYZ');
  b.sling.rx = _e.x;
  b.sling.ry = _e.y;
  b.sling.rz = _e.z;
};

/** A lazy twirl, one turn every 1.2 s; the head twitches round in quick held looks, the jaw chatters. */
const idle = (): Clip<B> =>
  clip<B>('idle', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    apply(b, ST);
    const br = Math.sin(p * 2);
    whirl(b, -2 * p, 0.12, 0.09, 0.42);
    b.root.pos(0, -0.01 * (1 + br), 0);
    b.body.rot(-0.03 * br, 0.05 * Math.sin(p), 0);
    b.chest.rot(-0.04 * br, 0.06 * Math.sin(p * 2 - 1.2), 0.03 * Math.sin(p + 1));
    // Twitchy looks: snapped to and held.
    const s = Math.sin(p + 0.4);
    const look = s > 0.35 ? 0.4 : s < -0.45 ? -0.35 : 0;
    b.head.rot(0.06 * br + (Math.sin(p * 3) > 0.7 ? -0.1 : 0), look + 0.04 * Math.sin(p * 7), 0.1 * Math.sin(p * 2 + 1));
    b.jaw.rot(0.14 * Math.max(0, Math.sin(p * 6)) ** 6);
    b.armL.rot(0.05 * br, 0, -0.04 * br);
    b.foreL.rot(-0.15 * Math.max(0, Math.sin(p * 3)) ** 2);
    b.bag.rot(0.04 * Math.sin(p * 2 - 0.8), 0, 0.03 * Math.sin(p - 1));
  }, (t) => {
    const br = Math.sin(phase(t, 2.4) * TAU * 2);
    return [1 + 0.015 * br, 1 - 0.02 * br, 1 + 0.015 * br];
  });

/** A skulking trot (two strides a loop), low and quick; the sling keeps turning, slower, over its head. */
const walk = (dur = 1.0): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const p = phase(t, dur) * TAU;
    const th = 2 * p;
    const sn = Math.sin(th), cs = Math.cos(th);
    apply(b, ST);
    whirl(b, -p, 0.1, 0.07, 0.3);
    b.legL.rot(-0.6 * sn, 0, -0.04 * cs);
    b.legR.rot(0.6 * sn, 0, 0.04 * cs);
    b.shinL.rot(0.8 * Math.max(0, cs) ** 1.3);
    b.shinR.rot(0.8 * Math.max(0, -cs) ** 1.3);
    b.root.pos(0, 0.04 * Math.abs(cs) - 0.03, 0).rot(0, 0, 0.05 * sn);
    b.body.rot(0.16 + 0.04 * Math.cos(2 * th), -0.12 * sn, -0.05 * sn);
    b.chest.rot(0.06, 0.06 * sn, 0.03 * sn);
    b.head.rot(-0.12 - 0.05 * Math.cos(2 * th - 0.6), -0.1 * sn + 0.2 * (Math.sin(p) > 0.6 ? 1 : 0), 0.05 * sn);
    b.armL.rot(0.45 * Math.sin(th - 0.5), 0, -0.08);
    b.foreL.rot(-0.3 * Math.max(0, -Math.sin(th - 0.9)));
    b.jaw.rot(0.08 * Math.max(0, Math.sin(th * 2)));
    b.bag.rot(-0.25 * Math.sin(th - 1.0), 0, 0.12 * Math.cos(th - 1.0));
  }, (t) => {
    const k = Math.cos(phase(t, dur) * TAU * 4);
    return [1 - 0.025 * k, 1 + 0.03 * k, 1 - 0.025 * k];
  });

/** The release: when the stone leaves the pouch (and the simulation launches it). */
const REL = 0.13;
/** The sling's turn rate going into the release (radians per second; negative swings forward on the right). */
const W0 = -TAU * 2.6;

/**
 * Comes in mid-whirl, wound back and turning hard; at 0.13 s it lunges and the stone flies; the sling snaps on
 * round and slows, the free claw dips into the bag and a new stone is in the pouch by the end.
 */
const attack = (dur = 0.85): Clip<B> =>
  clip<B>('attack', dur, false, (b, t) => {
    const wind: P = { body: [-0.08, 0.18, 0], chest: [-0.24, 0.42, 0.06], head: [-0.12, -0.42, 0], armR: [-0.35, 0, 0.15], foreR: [-0.2, 0, 0], armL: [-0.7, 0, -0.25], foreL: [-0.5, 0, 0], legL: [-0.4, 0, -0.06], shinL: [0.25, 0, 0], legR: [0.05, 0, 0.06], root: [0, 0, 0, 0, -0.02, -0.05] };
    const lunge: P = { body: [0.15, -0.15, 0], chest: [0.3, -0.35, -0.05], head: [0.05, 0.3, 0], jaw: [0.45, 0, 0], armR: [0.2, 0, -0.1], foreR: [0.1, 0, 0], armL: [0.55, 0, -0.35], foreL: [-0.2, 0, 0], legL: [-0.6, 0, -0.06], shinL: [0.45, 0, 0], legR: [0.25, 0, 0.06], shinR: [0.15, 0, 0], root: [0, 0, 0, 0, -0.07, 0.1] };
    const dip: P = { body: [0.2, -0.05, 0], chest: [0.25, -0.1, -0.12], head: [0.1, 0.25, -0.1], armR: [0.3, 0, 0], foreR: [0.2, 0, 0], armL: [0.2, 0, 0.05], foreL: [-0.35, 0, 0], legL: [-0.35, 0, -0.06], shinL: [0.38, 0, 0], root: [0, 0, 0, 0, -0.05, 0.04] };
    timeline(b, t, over([
      [0, wind],
      [REL, lunge, inCubic],
      [0.28, { ...lunge, body: [0.22, -0.18, 0], chest: [0.45, -0.42, -0.05], armR: [0.95, 0, -0.15], foreR: [0.5, 0, 0], jaw: [0.25, 0, 0] }, outQuad],
      [0.52, dip, inOut],
      [dur, {}, inOut],
    ]), { head: 0.02, foreR: 0.02, armL: 0.03 });
    // The sling: steady fast turn into the release, then the snap round and a slowing to rest at a full turn.
    const th = t <= REL ? W0 * (t - REL) : -TAU * outCubic(span(t, REL, dur, (k) => k));
    const tilt = t <= REL ? 0.15 - 0.1 * span(t, 0, REL, inQuad) : 0.05 + 0.07 * span(t, REL, dur, inOut);
    const fire = t <= REL ? 1.15 + 0.2 * span(t, 0, REL) : t < 0.6 ? 0.001 : 0.42 * span(t, 0.6, dur, outQuad);
    whirl(b, th, tilt, t <= REL ? 0.16 : 0.16 - 0.07 * span(t, REL, dur), fire);
    // The stone leaves at the release; a new one is in the pouch once the claw is back from the bag.
    b.stone.scale(t < REL ? 1 : t < 0.58 ? 0.001 : Math.max(0.001, span(t, 0.58, 0.72, outBack)));
    b.bag.rot(0.3 * envelope(t, 0.38, 0.5, 0.55, 0.75) + 0.12 * settle(t - REL, 3, 8) * (t > REL ? 1 : 0));
  }, (t) => {
    const k = t > REL ? Math.max(0, settle(t - REL, 3, 10)) : 0;
    return [1 + 0.03 * k, 1 - 0.045 * k, 1 + 0.03 * k];
  });

/** Struck: it flinches back, ears flat, the sling flopping round on its cords. */
const hit = (): Clip<B> =>
  clip<B>('hit', 0.38, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    whirl(b, -1.2 * span(t, 0, 0.38, outQuad) - 0.3 * k, 0.12 + 0.5 * k, 0.09, 0.42 * (1 - Math.abs(k)));
    b.root.pos(0, 0, -0.1 * k);
    b.body.rot(-0.4 * k, 0.15 * k, 0.08 * k);
    b.chest.rot(-0.25 * k, 0.1 * k);
    b.head.rot(-0.5 * k, -0.25 * k);
    b.jaw.rot(0.4 * Math.abs(k));
    b.armL.rot(0.4 * k, 0, -0.4 * k);
    b.armR.rot(0.3 * k, 0, 0.25 * k);
    b.bag.rot(0.35 * k);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.1 * k, 1 - 0.12 * k, 1 + 0.1 * k];
  });

/** Dazed: it reels on its feet, head lolling, the sling arm sagged and the sling dangling, swinging. */
const stun = (): Clip<B> =>
  clip<B>('stun', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    apply(b, ST, 0.6);
    b.root.rot(0.06 * Math.sin(p), 0, 0.12 * Math.cos(p));
    b.body.rot(0.2 + 0.08 * Math.sin(p * 2), 0, -0.1 * Math.cos(p));
    b.head.rot(0.25 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.25 * Math.sin(p + 0.8));
    b.jaw.rot(0.3);
    b.armL.rot(0.15 * Math.sin(p + 1.4), 0, -0.05);
    b.foreL.rot(0.1);
    b.armR.rot(-0.2 + 0.1 * Math.sin(p + 1.2), 0, -1.25 + 0.08 * Math.cos(p + 1.2));
    b.foreR.rot(0.35, 0, -0.35);
    b.bag.rot(0.1 * Math.sin(p - 1), 0, 0.12 * Math.cos(p - 1));
    hang(b, _d.set(0.35 * Math.sin(p - 1.6), -1, 0.3 * Math.cos(p - 1.6)));
    b.trail.scale(0.001);
  });

/** Struck dead: it arches back, the sling flung loose, crumples to its knees and pitches forward onto the ground. */
const die = (): Clip<B> =>
  clip<B>('die', 1.0, false, (b, t) => {
    const down: P = { body: [0.9, 0, 0.15], chest: [0.25, 0, 0], head: [0.3, 0.6, 0.3], jaw: [0.35, 0, 0], armL: [-2.1, 0, -0.5], foreL: [0.2, 0, 0], armR: [-0.5, 0, -1.9], foreR: [0.2, 0, -0.3], bag: [0.6, 0, 0.4], legL: [-0.55, 0, -0.2], shinL: [1.2, 0, 0], legR: [-0.45, 0, 0.2], shinR: [1.1, 0, 0], root: [0.5, 0, 0, 0, -0.33, 0.2] };
    timeline(b, t, [
      [0, ST],
      [0.12, { ...ST, body: [-0.5, 0.1, 0.1], chest: [-0.25, 0, 0], head: [-0.6, 0.2, 0], jaw: [0.6, 0, 0], armL: [-1.0, 0, -0.9], armR: [0.4, 0, 0.3], root: [0, 0, 0, 0, 0.02, -0.06] }, outExpo],
      [0.42, { body: [0.3, 0, 0.1], chest: [0.1, 0, 0], head: [0.4, 0.3, 0], jaw: [0.3, 0, 0], armL: [-0.3, 0, -0.3], armR: [0.1, 0, -1.2], foreR: [0.3, 0, 0], legL: [-1.4, 0, -0.2], shinL: [2.0, 0, 0], legR: [-1.3, 0, 0.2], shinR: [2.0, 0, 0], root: [0, 0, 0, 0, -0.3, 0] }, inQuad],
      [0.7, down, inQuad],
      [1.0, { ...down, body: [0.86, 0, 0.15], root: [0.54, 0, 0, 0, -0.34, 0.22] }, outBack],
    ], { head: 0.03, foreL: 0.02, foreR: 0.03, bag: 0.05 });
    // The sling: flung on round by the jolt, then dropped to dangle and lie along the ground.
    const fly = span(t, 0, 0.3, outQuad);
    const sx = 0.12 + 0.5 * fly, sy = -2.6 * fly;
    b.trail.scale(Math.max(0.001, 0.42 * (1 - span(t, 0, 0.2))));
    hang(b, _d.set(0.6, -1 + 0.8 * span(t, 0.55, 0.9), 0.4));
    // From flung round to hanging loose (the hang is worked out against the arm, so it is blended in, not added).
    const k = span(t, 0.2, 0.45, inOut);
    b.sling.rx = sx + (b.sling.rx - sx) * k;
    b.sling.ry = sy + (b.sling.ry - sy) * k;
    b.sling.rz *= k;
  }, (t) => {
    const k = t > 0.7 ? Math.max(0, settle(t - 0.7, 3, 9)) : 0;
    return [1 + 0.08 * k, 1 - 0.1 * k, 1 + 0.08 * k];
  });

/** Its clips (see models/slinger.ts for the bones). */
export const slingerClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { slinger: slingerClips };
